import { getValue } from '~/js/utils/selectors';
import { addClass } from '~/js/utils/class-management';

interface WidgetValidationOptions {
  onSubmit(event: Event): void;
}

const schema = {
  origin: {
    required: true,
    message: 'Selecione sua origem',
    lookTo: 'data-origin',
  },
  destination: {
    required: true,
    message: 'Selecione seu destino',
    lookTo: 'data-destination',
  },
  'initial-date': {
    required: true,
    message: 'Selecione a data de partida',
    when: {
      field: 'switch-date-selection',
      equal: 'ida-e-volta',
    },
  },
  'last-date': {
    required: true,
    message: 'Selecione a data de volta',
    when: {
      field: 'switch-date-selection',
      equal: 'ida-e-volta',
    },
  },
};

type SchemaField = keyof typeof schema;

function setInputAInvalid(input: HTMLInputElement) {
  input.setAttribute('aria-invalid', 'true');
}

function setInputAsValid(input: HTMLInputElement) {
  input.removeAttribute('aria-invalid');
  input.removeAttribute('aria-describedby');
}

function appendLabelError(input: HTMLInputElement, message: string) {
  const isInputAlreadyInvalid = input.getAttribute('aria-invalid') === 'true';
  if (isInputAlreadyInvalid) return;
  const label = document.createElement('span');
  const inputId = input.getAttribute('name') || '';
  const id = `label-error-for-${inputId}`;
  label.setAttribute('role', 'alert');
  label.setAttribute('aria-live', 'assertive');
  label.setAttribute('data-errors-for', inputId);
  label.setAttribute('id', id);
  addClass(label, 'error-label');
  label.innerHTML = message;

  const icon = document.createElement('i');
  addClass(icon, 'icon-exclamation-circle');
  addClass(icon, 'error-icon');

  input.setAttribute('aria-describedby', id);
  input.parentElement?.insertAdjacentElement('beforeend', icon);
  input.parentElement?.insertAdjacentElement('beforeend', label);
}

function removeLabelError(input: HTMLInputElement) {
  const inputId = input.getAttribute('name');
  const labelError = document.querySelector(`#label-error-for-${inputId}`);
  const iconError = input.parentElement?.querySelector('.error-icon');
  if (labelError && iconError) {
    labelError.parentNode?.removeChild(labelError);
    iconError.parentNode?.removeChild(iconError);
  }
}

class WidgetValidation {
  private inputs!: NodeListOf<HTMLInputElement>;
  isValid!: boolean;
  constructor(
    private readonly form: HTMLFormElement,
    private readonly options: WidgetValidationOptions
  ) {
    this.isValid = false;
    this.getInputs();
    this.initialize();
  }

  private getInputs() {
    this.inputs = this.form.querySelectorAll('input:not([type="radio"])');
  }

  private toggleValid(valid: boolean) {
    this.isValid = valid;
  }

  public getValid() {
    return this.isValid;
  }

  public validateForm() {
    const status = Array.from(this.inputs).map(this.validateField);
    const allValid = status.every((isValid) => isValid);
    this.toggleValid(allValid);
  }

  private attachEventsToInputs() {
    this.inputs.forEach((input) => {
      input.addEventListener('blur', () => {
        setTimeout(() => this.validateField(input), 200);
      });
      input.addEventListener('focus', () => {
        removeLabelError(input);
        setInputAsValid(input);
      });
    });
  }

  private attachEventToForm() {
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.validateForm();
      if (this.isValid) this.options.onSubmit(e);
    });
  }

  public validateField = (field: HTMLInputElement) => {
    const fieldName = field.getAttribute('name') as SchemaField;
    const fieldSchema = schema[fieldName];

    let isFieldValid = false;

    let valueToCompare = field.value;

    if ('lookTo' in fieldSchema) {
      valueToCompare = field.getAttribute(fieldSchema.lookTo) as string;
    }

    if (fieldSchema.required) {
      if ('when' in fieldSchema) {
        const dependsInput = `input[name="${fieldSchema.when.field}"]:checked`;
        const dependsValue = getValue(dependsInput, this.form);
        isFieldValid =
          dependsValue !== fieldSchema.when.equal
            ? true
            : valueToCompare !== '';
      } else {
        isFieldValid = Boolean(valueToCompare);
      }
    }

    if (!isFieldValid) {
      appendLabelError(field, fieldSchema.message);
      setInputAInvalid(field);
    } else {
      setInputAsValid(field);
      removeLabelError(field);
    }

    return isFieldValid;
  };

  private initialize() {
    this.attachEventsToInputs();
    this.attachEventToForm();
  }
}

export default WidgetValidation;
